import {
  EntityId,
  JobPositionTask,
  OrderBy,
  RWMemberRoleKeys,
  RWMemberActiveJobApplicationCategory,
  Wallet,
  RWMemberRole,
  RwMemberActivity,
} from '@restworld/utility-types';
import { ENDPOINTS } from './../../endpoints';
import { useMemo } from 'react';
import { Service } from './../base';
import dynamicAxios from './../../utils/axios';
import { formatISO } from 'date-fns';
import {
  CsmAgenda,
  IndexActiveJobApplications,
  IndexRwMembers,
  RwMemberWorkload,
  RWMemberUpdatePayloadType,
  JpsWithLowShortlisted,
  RwMemberActivitiesParams,
} from './types';
import useServices from '../../hooks/useServices';

class ServiceRWMembers extends Service {
  fetchRWMembers({
    limit,
    offset,
    roles,
    excludingUserId,
    searchVector,
  }: {
    limit?: number;
    offset?: number;
    roles?: RWMemberRoleKeys[];
    excludingUserId?: string;
    searchVector?: string;
  }) {
    const params: any = roles
      ? {
          role: roles,
        }
      : {
          limit,
          offset,
        };
    params.excluding_user_id = excludingUserId;
    params.search_vector = searchVector;
    return this.axiosInstance.get<IndexRwMembers>(ENDPOINTS.rwMembers.root, {
      params,
    });
  }

  createRwMember(data: RWMemberUpdatePayloadType) {
    return this.axiosInstance.post(ENDPOINTS.rwMembers.root, data);
  }

  updateRwMember(rwMemberId: EntityId, data: RWMemberUpdatePayloadType) {
    return this.axiosInstance.put(`${ENDPOINTS.rwMembers.root}/${rwMemberId}`, data);
  }

  fetchRoles({ user_id }: { user_id: EntityId }) {
    return this.axiosInstance.get<RWMemberRole[]>(`${ENDPOINTS.rwMembers.root}/roles`, {
      params: { id: user_id },
    });
  }

  fetchAvailableRoles() {
    return this.axiosInstance.get<RWMemberRole[]>(`${ENDPOINTS.rwMembers.root}/available_roles`);
  }

  deleteRwMember(rwMemberId: EntityId) {
    return this.axiosInstance.delete(`${ENDPOINTS.rwMembers.root}/${rwMemberId}`);
  }

  getWallet({ csmId, referenceDate }: { csmId?: EntityId; referenceDate?: Date }) {
    return this.axiosInstance.get<Wallet>(ENDPOINTS.rwMembers.wallet, {
      params: {
        csm_id: csmId,
        date: referenceDate ? formatISO(referenceDate, { representation: 'date' }) : undefined,
      },
    });
  }

  getActiveApplications({
    csmId,
    count,
    limit,
    offset,
    category,
  }: {
    csmId?: EntityId;
    count?: boolean;
    limit?: number;
    offset?: number;
    category?: RWMemberActiveJobApplicationCategory;
  }) {
    return this.axiosInstance.get<IndexActiveJobApplications>(ENDPOINTS.rwMembers.activeApplications, {
      params: { csm_id: csmId, count, limit, offset, category },
    });
  }

  getAgenda({
    day,
    rwMemberId,
    overdue,
    nextWeek,
    monthlyWorkload,
    orderBy,
  }: {
    day?: string;
    rwMemberId?: EntityId;
    overdue?: boolean;
    nextWeek?: boolean;
    monthlyWorkload?: string;
    orderBy?: OrderBy;
  }) {
    return this.axiosInstance.get<CsmAgenda>(ENDPOINTS.rwMembers.agenda, {
      params: {
        rw_member_id: rwMemberId,
        day,
        overdue,
        next_week: nextWeek,
        monthly_workload: monthlyWorkload,
        order_by: orderBy?.variable ? `${orderBy.order}:${orderBy.variable}` : undefined,
      },
    });
  }

  getAssociatedTasks({ referentContactId, csmId }: { referentContactId: number; csmId?: EntityId }) {
    return this.axiosInstance.get<JobPositionTask[]>(ENDPOINTS.rwMembers.associatedTasks, {
      params: { csm_id: csmId, referent_contact_id: referentContactId },
    });
  }

  getRwMemberWorkload({ lowestWorkload }: { lowestWorkload?: boolean }) {
    return this.axiosInstance.get<RwMemberWorkload[]>(ENDPOINTS.rwMembers.workload, {
      params: { lowest_workload: lowestWorkload },
    });
  }

  getRestaurantReferentAssociatedTasks({
    referentUserContactIds,
    csmId,
  }: {
    referentUserContactIds: string[];
    csmId?: EntityId;
  }) {
    return this.axiosInstance.get<JobPositionTask[]>(ENDPOINTS.rwMembers.associatedTasks, {
      params: { csm_id: csmId, referent_user_contact_ids: referentUserContactIds },
    });
  }

  getAlertJpsWithLowShortlisted({
    csmId,
    thresholdDays,
    thresholdShortlisted,
  }: {
    csmId?: EntityId;
    limit?: number;
    offset?: number;
    thresholdDays?: number;
    thresholdShortlisted?: number;
  }) {
    return this.axiosInstance.get<JpsWithLowShortlisted[]>(ENDPOINTS.rwMembers.alertJpsWithLowShortlisted, {
      params: {
        csm_id: csmId,
        threshold_days: thresholdDays,
        threshold_shortlisted: thresholdShortlisted,
      },
    });
  }

  uploadPicture(rwMemberId: EntityId, picture: File) {
    const formData = new FormData();
    formData.append('picture', picture);
    formData.append('id', rwMemberId);
    return this.axiosInstance.post(`${ENDPOINTS.rwMembers.root}/picture`, formData);
  }

  deletePicture(rwMemberId: EntityId) {
    return this.axiosInstance.delete(`${ENDPOINTS.rwMembers.root}/picture`, {
      params: { id: rwMemberId },
    });
  }

  getRwMemberActivities(params?: RwMemberActivitiesParams) {
    const query = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          query.append(key, String(value));
        }
      });
    }

    const url = `${ENDPOINTS.rwMembers.root}/activities${query.toString() ? `?${query.toString()}` : ''}`;

    return this.axiosInstance.get<RwMemberActivity[]>(url);
  }

  employerUnpaidInvoicesAlert({
    organizationId,
    type,
  }: {
    organizationId?: string;
    type?: 'employer_unpaid_invoice?';
  }) {
    return this.axiosInstance.get<boolean>(ENDPOINTS.rwMembers.alerts, {
      params: { organization_id: organizationId, type },
    });
  }
}

export function useRwMembersService() {
  const { baseUrl, jwt } = useServices();
  return useMemo(() => new ServiceRWMembers(dynamicAxios(baseUrl, jwt)), [baseUrl, jwt]);
}

export default useRwMembersService;

export * from './types';
